import { useSlots } from '@lib/hooks/useSlots'
import clsx from 'clsx'
import React, { FC, PropsWithChildren, useId } from 'react'

import Checkbox from '../Checkbox'
import PhoneNumberInput from '../PhoneNumberInput'
import Select from '../Select'
import TextInput from '../TextInput/TextInput'
import s from './FormControl.module.scss'
import FormControlCaption from './_FormControlCaption'
import { FormControlContextProvider } from './_FormControlContext'
import FormControlLabel from './_FormControlLabel'
import FormControlValidation from './_FormControlValidation'
type Props = {
  /**
   * If true, the user must specify a value for the input before the owning form can be submitted
   */
  required?: boolean
  disabled?: boolean
  flexAlign?: 'row' | 'row-reverse'
} & (
  | {
      layout?: 'vertical'
      floatingLabel?: boolean
    }
  | {
      layout?: 'horizontal'
      floatingLabel?: never
    }
)
type NativeAttrs = Omit<React.HTMLAttributes<HTMLDivElement>, keyof Props>

export type FormControlProps = Props & NativeAttrs

const FormControl: FC<PropsWithChildren<FormControlProps>> = ({
  children,
  className,
  required,
  layout = 'vertical',
  id: idProp,
  floatingLabel,
  disabled: disabledProp,
  flexAlign,
  ...props
}) => {
  const [slots, childrenWithoutSlots] = useSlots(children, {
    label: FormControlLabel,
    caption: FormControlCaption,
    validation: FormControlValidation,
  })

  const expectedInputComponents = [
    TextInput,
    Select,
    PhoneNumberInput,
    Checkbox,
  ]
  const id = useId()
  const disabled = disabledProp
  const validationMessageId = slots.validation
    ? `${id}-validationMessage`
    : undefined
  const captionId = slots.caption ? `${id}-caption` : undefined
  const validationStatus = slots.validation?.props.variant
  const InputComponent = childrenWithoutSlots.find((child) =>
    expectedInputComponents.some(
      (inputComponent) =>
        React.isValidElement(child) && child.type === inputComponent,
    ),
  )

  const isChoiceInput =
    React.isValidElement(InputComponent) && InputComponent.type === Checkbox

  const floatingLabelEnabled =
    floatingLabel &&
    React.isValidElement(InputComponent) &&
    InputComponent.type === TextInput

  return (
    <FormControlContextProvider value={{ id, required, validationMessageId }}>
      {layout === 'horizontal' || isChoiceInput ? (
        <div
          className={clsx(s.formControl, className, s.horizontal, {
            [s.choiceInput]: isChoiceInput && flexAlign === 'row-reverse',
          })}
          {...props}
        >
          {!isChoiceInput && (
            <div className={s.labelContainer}>
              {slots.label}
              {slots.caption}
            </div>
          )}

          {isChoiceInput && (
            <div className={clsx(s.labelContainer, s.isChoice)}>
              {slots.label}
              {slots.validation ? slots.validation : null}
            </div>
          )}

          <div className={s.inputContainer}>
            {React.isValidElement(InputComponent) &&
              React.cloneElement(
                InputComponent,
                Object.assign(
                  {
                    id,
                    required,
                    disabled,
                    validationStatus,
                    ['aria-describedby']: [validationMessageId, captionId]
                      .filter(Boolean)
                      .join(' '),
                  },
                  InputComponent.props,
                ),
              )}
            {isChoiceInput && slots.caption ? slots.caption : null}
            {!isChoiceInput && slots.validation ? slots.validation : null}
          </div>
        </div>
      ) : (
        <div
          className={clsx(s.formControl, className, {
            [s.floating]: floatingLabelEnabled,
          })}
          {...props}
        >
          {slots.label}
          {React.isValidElement(InputComponent) &&
            React.cloneElement(
              InputComponent,
              Object.assign(
                {
                  id,
                  required,
                  disabled,
                  validationStatus,
                  ['aria-describedby']: [validationMessageId, captionId]
                    .filter(Boolean)
                    .join(' '),
                },
                InputComponent.props,
              ),
            )}
          {slots.validation ? slots.validation : null}
          {slots.caption}
        </div>
      )}
    </FormControlContextProvider>
  )
}

FormControl.displayName = 'FormControl'

export default Object.assign(FormControl, {
  Label: FormControlLabel,
  Caption: FormControlCaption,
  Validation: FormControlValidation,
})
